import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import SalesBoardTiles, { ukDate, londonTime, feedIsStale } from '../components/SalesBoardTiles';

// The office sales screen. The figures update themselves from the Autovolt
// feed every 15 minutes. Anyone switched on for the Sales Board sees the board;
// only admins see the forms to set targets, enter opening figures, and correct
// a day by hand (the server enforces the same split).

const REFRESH_MS = 60 * 1000;

const londonTodayIso = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

// The London date before today, for the opening figures' default and limit.
const londonYesterdayIso = () => {
  const d = new Date(`${londonTodayIso()}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

// Accept figures as people paste them from a spreadsheet: "£35,224.40" -> "35224.40"
const cleanMoney = (v) => String(v ?? '').replace(/[£,\s]/g, '');

const money2 = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);

const SalesBoard = () => {
  const [board, setBoard] = useState(null);
  const [days, setDays] = useState([]);
  const [targets, setTargets] = useState({ day: '', week: '', month: '', year_target: '' });
  const [opening, setOpening] = useState({ as_of: londonYesterdayIso(), week_to_date: '', month_to_date: '', year_to_date: '' });
  const [dayForm, setDayForm] = useState({ date: londonTodayIso(), order_value: '', orders: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');
  // Each form shows its own result under its button, where the person is looking.
  const [notice, setNotice] = useState({ form: '', type: '', text: '' });

  const year = Number(londonTodayIso().slice(0, 4));
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const loadBoard = useCallback(async () => {
    try {
      const res = await api.get('/api/sales/board');
      setBoard(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load the sales board');
    }
  }, []);

  const loadDays = useCallback(async () => {
    const res = await api.get('/api/sales/days');
    setDays(res.data);
  }, []);

  useEffect(() => {
    (async () => {
      await loadBoard();
      if (!isAdmin) return;
      try {
        await loadDays();
        const [t, o] = await Promise.all([
          api.get('/api/sales/targets', { params: { year } }),
          api.get('/api/sales/opening'),
        ]);
        if (t.data) setTargets({ day: t.data.day, week: t.data.week, month: t.data.month, year_target: t.data.year_target });
        if (o.data) setOpening(o.data);
      } catch {
        setError('Failed to load the sales settings');
      }
    })();
    const timer = setInterval(loadBoard, REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadBoard, loadDays, year, isAdmin]);


  const save = async (what, fn, okText) => {
    setSaving(what);
    setNotice({ form: '', type: '', text: '' });
    try {
      await fn();
      setNotice({ form: what, type: 'success', text: okText });
      await Promise.all([loadBoard(), loadDays()]);
    } catch (err) {
      setNotice({ form: what, type: 'error', text: err.response?.data?.error || 'Save failed. Check the figures and try again.' });
    } finally {
      setSaving('');
    }
  };

  const Notice = ({ form }) => (notice.form === form
    ? <div className={`alert alert-${notice.type}`} style={{ marginTop: 14, marginBottom: 0 }}>{notice.text}</div>
    : null);

  const saveDay = (e) => {
    e.preventDefault();
    const date = dayForm.date;
    save('day', async () => {
      await api.put(`/api/sales/days/${date}`, { order_value: cleanMoney(dayForm.order_value), orders: dayForm.orders });
      setDayForm(f => ({ ...f, order_value: '', orders: '' }));
    }, `Saved ${ukDate(date)}`);
  };

  const saveTargets = (e) => {
    e.preventDefault();
    save('targets', async () => {
      const body = Object.fromEntries(Object.entries(targets).map(([k, v]) => [k, cleanMoney(v)]));
      await api.put(`/api/sales/targets/${year}`, body);
    }, `Targets for ${year} saved`);
  };

  const saveOpening = (e) => {
    e.preventDefault();
    save('opening', async () => {
      const res = await api.put('/api/sales/opening', {
        as_of: opening.as_of,
        week_to_date: cleanMoney(opening.week_to_date),
        month_to_date: cleanMoney(opening.month_to_date),
        year_to_date: cleanMoney(opening.year_to_date),
      });
      setOpening(res.data);
    }, `Opening figures saved, up to and including ${ukDate(opening.as_of)}`);
  };

  const field = (setter) => (e) => { const { name, value } = e.target; setter(prev => ({ ...prev, [name]: value })); };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Sales Board</h1>
          <p className="page-subtitle">
            Today's figure comes from Autovolt every 15 minutes and everything else is worked out from it.
          </p>
        </div>
        <Link to="/sales-board/tv" className="btn btn-primary">Open office screen</Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {board && (
        <>
          <div className={`alert ${feedIsStale(board) ? 'alert-error' : 'alert-info'}`}>
            {board.today_updated
              ? <>Today's figure was last updated at {londonTime(board.today_updated)} ({board.today_source === 'feed' ? 'from Autovolt' : 'entered by hand'}){board.today_orders !== null && `, ${board.today_orders} orders`}.</>
              : <>No figure for today yet.</>}
            {feedIsStale(board) && <> The Autovolt feed has not posted for over 30 minutes{board.last_feed_at ? ` (last at ${londonTime(board.last_feed_at)} on ${new Date(board.last_feed_at).toLocaleDateString('en-GB', { timeZone: 'Europe/London' })})` : ''}.</>}
            {board.targets_carried_over && <> No targets are set for {board.today.slice(0, 4)}, so {board.targets_year}'s are being used.{isAdmin && ' Set them below.'}</>}
          </div>
          <SalesBoardTiles board={board} />
        </>
      )}

      {isAdmin && (<>
      <div className="sb-admin-grid">
        <div className="card">
          <div className="card-header"><h2 className="card-title">Set a day's sales by hand</h2></div>
          <div className="card-body">
            <p className="sb-help">For when the feed was down. A figure entered for today is replaced by Autovolt's next reading.</p>
            <form onSubmit={saveDay}>
              <div className="form-group">
                <label className="form-label" htmlFor="sb-date">Date</label>
                <input id="sb-date" className="form-control" type="date" name="date" max={londonTodayIso()} value={dayForm.date} onChange={field(setDayForm)} required />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="sb-value">Order value inc. surcharge (£)</label>
                <input id="sb-value" className="form-control" type="text" inputMode="decimal" name="order_value" value={dayForm.order_value} onChange={field(setDayForm)} required />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="sb-orders">Orders (optional)</label>
                <input id="sb-orders" className="form-control" type="number" step="1" min="0" name="orders" value={dayForm.orders} onChange={field(setDayForm)} />
              </div>
              <button className="btn btn-primary" disabled={saving === 'day'}>{saving === 'day' ? 'Saving' : 'Save day'}</button>
              <Notice form="day" />
            </form>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h2 className="card-title">Targets for {year}</h2></div>
          <div className="card-body">
            <form onSubmit={saveTargets}>
              {[['day', 'Daily target'], ['week', 'Weekly target'], ['month', 'Monthly target'], ['year_target', 'Yearly target']].map(([k, label]) => (
                <div className="form-group" key={k}>
                  <label className="form-label" htmlFor={`sb-t-${k}`}>{label} (£)</label>
                  <input id={`sb-t-${k}`} className="form-control" type="text" inputMode="decimal" name={k} value={targets[k]} onChange={field(setTargets)} required />
                </div>
              ))}
              <button className="btn btn-primary" disabled={saving === 'targets'}>{saving === 'targets' ? 'Saving' : 'Save targets'}</button>
              <Notice form="targets" />
            </form>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h2 className="card-title">Opening figures</h2></div>
          <div className="card-body">
            <p className="sb-help">
              Sales made before the board was switched on. Enter the totals up to and including the date below
              (for example the old spreadsheet's figures, less today's). Today always comes from Autovolt, so the
              date must be yesterday or earlier. Days on or before that date are then taken from these figures,
              not from the daily list.
            </p>
            <form onSubmit={saveOpening}>
              <div className="form-group">
                <label className="form-label" htmlFor="sb-o-date">Up to and including (yesterday at the latest)</label>
                <input id="sb-o-date" className="form-control" type="date" name="as_of" max={londonYesterdayIso()} value={opening.as_of || ''} onChange={field(setOpening)} required />
              </div>
              {[['week_to_date', 'Week to date (£)'], ['month_to_date', 'Month to date (£)'], ['year_to_date', 'Year to date (£)']].map(([k, label]) => (
                <div className="form-group" key={k}>
                  <label className="form-label" htmlFor={`sb-o-${k}`}>{label}</label>
                  <input id={`sb-o-${k}`} className="form-control" type="text" inputMode="decimal" name={k} value={opening[k] ?? ''} onChange={field(setOpening)} required />
                </div>
              ))}
              <button className="btn btn-primary" disabled={saving === 'opening'}>{saving === 'opening' ? 'Saving' : 'Save opening figures'}</button>
              <Notice form="opening" />
            </form>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header"><h2 className="card-title">Daily figures this month</h2></div>
        <div className="table-wrap">
          {days.length === 0 ? (
            <div className="empty-state"><p>No daily figures yet this month.</p></div>
          ) : (
            <table>
              <thead><tr><th>Date</th><th>Order value inc. surcharge</th><th>Orders</th><th>Source</th><th>Updated</th></tr></thead>
              <tbody>
                {days.map(d => (
                  <tr key={d.sale_date}>
                    <td>{ukDate(d.sale_date)}</td>
                    <td>{money2(d.order_value)}</td>
                    <td>{d.orders ?? ''}</td>
                    <td>
                      {d.source === 'feed' ? 'Autovolt' : `By hand${d.updated_by_name ? ` (${d.updated_by_name})` : ''}`}
                      {opening.as_of && d.sale_date <= opening.as_of && <span className="sb-note"> (not counted, covered by opening figures)</span>}
                    </td>
                    <td>{londonTime(d.as_at || d.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      </>)}
    </div>
  );
};

export default SalesBoard;
